// Centralized GA4 analytics layer. ALL Google Analytics events go through here, so there's one
// tested place for the gtag guard, error handling, and telemetry breadcrumb. gtag.js is installed
// sitewide in public/index.html; every sender degrades silently if it's missing (ad-blocker, not
// yet loaded) and never throws.
//
// (Replaces the legacy CallGoogleAnalytics / CallAppInsights that lived in paymentModal.actions.js.
// Note the old helper also dumped ALL of localStorage into every event — removed here.)

const hasGtag = () => typeof window.gtag === "function";

// 5%-sampled breadcrumb into App Insights, mirroring the legacy CallAppInsights. Internal only.
const sendAppInsights = properties => {
  if (typeof window.appInsights !== "object") return;
  try {
    window.appInsights.trackEvent({ name: "GoogleAnalytics", properties });
  } catch (err) {
    console.log(err);
  }
};

/**
 * Low-level primitive: send any GA4 event with the explicit params only (no localStorage dump).
 * Guards on gtag, never throws, and emits a 5%-sampled App Insights breadcrumb.
 * @param {string} name    GA4 event name (reserved like "purchase"/"page_view", or a custom name).
 * @param {Object} params  Event params.
 */
export const sendGaEvent = (name, params = {}) => {
  if (!hasGtag()) return;
  try {
    window.gtag("event", name, params);
    if (Math.random() >= 0.95)
      sendAppInsights({ call: "sendGaEvent", freq: "0.95", name, params });
  } catch (err) {
    sendAppInsights({ call: "sendGaEvent", freq: "0.95", error: err, name });
    console.log(err);
  }
};

/**
 * GA4 reserved `purchase` event.
 * @param {Object} p
 * @param {string|number} p.transactionId  Order id — the dedupe key GA4 AND Google Ads both use.
 * @param {number}   p.value               Conversion value.
 * @param {string}   [p.currency="USD"]
 * @param {Array}    [p.items=[]]
 */
export const sendGaPurchase = ({ transactionId, value, currency = "USD", items = [] }) =>
  sendGaEvent("purchase", {
    transaction_id: String(transactionId),
    value: Number(value) || 0,
    currency,
    items
  });

/**
 * GA4 virtual `page_view` for an SPA route change (the app emits none on client-side navigation),
 * so pages like /success become visible and audience-targetable in GA4.
 * @param {string} path  e.g. "/success"
 */
export const sendGaPageView = path =>
  sendGaEvent("page_view", {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title
  });

/**
 * Account-registration event. Preserves the original "Registered" event name + params that were
 * previously fired via CallGoogleAnalytics in register.actions.js (kept as-is so GA reporting is
 * unaffected). GA4's recommended name for this is `sign_up` — a future rename, not a move-time change.
 */
export const sendGaRegistered = () =>
  sendGaEvent("Registered", { event_category: "User", label: "New", value: 1 });
